/* =========================================================================
   error-boundary.tsx — Enterprise-grade error boundaries with fallbacks.
   ========================================================================= */

import React from "react";
import { errorReporter } from "./ws-observability/error-reporter";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError?.(error, errorInfo);
    errorReporter.capture(error, { componentStack: errorInfo.componentStack });
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? <DefaultFallback error={this.state.error} />
      );
    }
    return this.props.children;
  }
}

function DefaultFallback({ error }: { error?: Error }) {
  return (
    <div
      className="page"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100dvh",
        gap: 16,
      }}
    >
      <h1>Something went wrong</h1>
      <p>{error?.message ?? "An unexpected error occurred."}</p>
      <button
        type="button"
        className="btn primary"
        onClick={() => globalThis.location.reload()}
      >
        Reload
      </button>
    </div>
  );
}

/** Screen-level fallback: shows a contained error card instead of white-screening the whole app. */
export function ScreenErrorBoundary({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <ErrorBoundary
      fallback={
        <div
          className="page"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "60dvh",
            gap: 16,
          }}
        >
          <div style={{ fontSize: 48, lineHeight: 1 }}>⚠️</div>
          <h2>This view failed to load</h2>
          <p className="p-small" style={{ maxWidth: 400, textAlign: "center" }}>
            Something went wrong while rendering this screen. Your data is safe.
          </p>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn primary"
              onClick={() => globalThis.location.reload()}
            >
              Reload page
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => globalThis.history.back()}
            >
              Go back
            </button>
          </div>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}

/** Chart-level fallback: minimal inline replacement. */
export function ChartErrorBoundary({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <ErrorBoundary
      fallback={
        <div
          className="card card-pad"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            minHeight: 200,
            color: "hsl(var(--c-foreground-muted))",
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M15 9l-6 6M9 9l6 6" />
          </svg>
          <span className="p-small">Chart unavailable</span>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}

/** Export-level fallback: wraps export buttons so a PDF/Excel crash doesn't kill the page. */
export function ExportErrorBoundary({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <ErrorBoundary
      fallback={
        <div
          className="pill danger"
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <svg
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M15 9l-6 6M9 9l6 6" />
          </svg>
          Export failed
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}
